import { Config, ConfigSchemas } from '@/types';
import { DEFAULT_CONFIG } from './constants';
import { getPreset } from './presets';

/**
 * Converts function strings to actual functions for defaultValue fields
 * Handles:
 * - Full function strings: "() => analytics.user().anonymousId()" or "(pageContext, customContext) => ..."
 * - Function bodies: "analytics.user().anonymousId()" (will be wrapped with "() =>")
 * Note: External dependencies like 'analytics' must be available in widget context
 * The function is only called on form submit, not during config validation
 */
function convertFunctionString(
  value: Config.ValueTypes.StringOrContextResultFunction | undefined
): Config.ValueTypes.StringOrContextResultFunction | undefined {
  if (typeof value === 'string' && value.trim() !== '') {
    // Check if it's already a function string (contains => or function keyword)
    const isFunctionString = value.includes('=>') || value.includes('function');
    
    if (isFunctionString) {
      try {
        return new Function(
          'pageContext',
          'customContext',
          `
          const fn = ${value};
          return typeof fn === 'function' ? fn : () => fn;
        `
        ) as (pageContext: unknown, customContext: unknown) => string;
      } catch (error) {
        console.warn(
          '[Widget Config] Could not parse function string, keeping as string:',
          error
        );
        return value;
      }
    }
    
    // If it's a plain string but looks like a function body (has method calls with parentheses)
    // Function bodies should always be wrapped with () => since we're defining functions
    // Pattern: contains parentheses and dots/brackets (method calls or property access)
    const looksLikeFunctionBody = 
      value.includes('(') && value.includes(')') && 
      (value.includes('.') || value.includes('['));
    
    if (looksLikeFunctionBody) {
      try {
        // Always wrap with () => to make it a proper function
        // This ensures function bodies are always valid function definitions
        const wrappedFunction = `() => ${value}`;
        return new Function(
          'pageContext',
          'customContext',
          `
          const fn = ${wrappedFunction};
          return typeof fn === 'function' ? fn : () => fn;
        `
        ) as (pageContext: unknown, customContext: unknown) => string;
      } catch (error) {
        // If wrapping fails, keep as string (might be a plain string value like "some-value()")
        console.warn(
          '[Widget Config] Could not wrap function body, keeping as string:',
          error
        );
        return value;
      }
    }
  }
  return value;
}

// Deep merge function for theme configuration
function deepMerge<T>(
  base: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: any
): T {
  const result = { ...base };

  // First, merge properties from base
  for (const key in base) {
    const baseValue = result[key];
    const targetValue = target[key];

    if (
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue) &&
      baseValue &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue)
    ) {
      result[key] = deepMerge(baseValue, targetValue);
    } else if (Array.isArray(targetValue) && Array.isArray(baseValue)) {
      // For arrays, replace entirely (fields array should come from user config)
      result[key] = targetValue as T[Extract<keyof T, string>];
    } else if (targetValue !== undefined) {
      result[key] = targetValue as T[Extract<keyof T, string>];
    }
  }

  // Then, add properties that exist in target but not in base
  // This ensures properties like defaultValue on fields are preserved
  if (target && typeof target === 'object' && !Array.isArray(target) && typeof result === 'object' && result !== null) {
    for (const key in target) {
      if (!(key in result)) {
        (result as Record<string, unknown>)[key] = target[key];
      }
    }
  }

  return result;
}

export function mergeConfig(userConfig: Config.UserWidget): Config.Widget {
  // TODO: Should probably show some kind of message to the user if the config is invalid

  // Apply preset if specified
  if (userConfig.preset) {
    const preset = getPreset(userConfig.preset);
    if (preset) {
      // Merge preset into userConfig (user values take precedence)
      // Type assertion needed because preset.theme is DeepPartial but deepMerge handles it correctly
      userConfig = {
        ...userConfig,
        customComponents: {
          ...preset.customComponents,
          ...userConfig.customComponents,
        },
        theme: deepMerge(
          preset.theme || {},
          userConfig.theme || {}
        ) as Config.UserWidget['theme'],
        ui: {
          ...preset.ui,
          ...userConfig.ui,
        },
      };
    } else {
      console.warn(
        `[Widget Config] Unknown preset "${userConfig.preset}". ` +
          `Available presets: peptalk-form`
      );
    }
  }

  // Debug logs removed for production

  // Check for hidden/function fields missing required fields and try to fix them
  if (userConfig.features?.chat?.fields) {
    userConfig.features.chat.fields.forEach((field: Config.FieldWithMetadata, idx) => {
      // Hidden fields must have defaultValue
      if (field.type === 'hidden' && !field.defaultValue) {
        const hasFunctionFlag = field._defaultValueIsFunction;
        console.warn(
          `[Widget Config] hidden field "${field.name}" (index ${idx}) is missing defaultValue but has _defaultValueIsFunction=${hasFunctionFlag}. ` +
          `This field was likely saved incorrectly in the builder.`
        );
        // Add fallback - validation will catch this and show proper error
        field.defaultValue = ''; // Empty string for hidden fields
      }
      // Function fields must have function field (not defaultValue)
      // Only warn if they have _defaultValueIsFunction flag (indicating old format)
      if (field.type === 'function' && field._defaultValueIsFunction && !field.function) {
        console.warn(
          `[Widget Config] function field "${field.name}" (index ${idx}) has _defaultValueIsFunction flag but is missing function field. ` +
          `This field was likely saved incorrectly in the builder.`
        );
        // Validation will catch this and show proper error
      }
    });
    
    // Convert function strings to actual functions for 'function' type fields BEFORE validation
    // Function type fields use 'function' field (not 'defaultValue')
    // We need to convert them before Zod validation runs
    userConfig.features.chat.fields = userConfig.features.chat.fields.map((field: Config.FieldWithMetadata) => {
      // Function type fields: convert defaultValue string to function field
      if (field.type === 'function') {
        // Check if defaultValue exists (old format) or function exists (new format)
        const functionString = field.function || field.defaultValue;
        if (typeof functionString === 'string') {
          const converted = convertFunctionString(functionString);
          // If conversion succeeded, set the function field
          if (typeof converted === 'function') {
            const updatedField = { ...field };
            updatedField.function = converted;
            // Remove defaultValue if it exists (migrate from old format)
            delete updatedField.defaultValue;
            return updatedField;
          }
          // If conversion failed, log a warning but let validation catch it
          console.warn(
            `[Widget Config] Function field "${field.name}" function could not be converted. ` +
            `Value: ${functionString}. Validation will fail.`
          );
        }
      }
      return field;
    });
  }

  // Validate the userConfig against the schema
  const parsedUserConfig = ConfigSchemas.UserWidget.parse(userConfig);

  // Merge the default config with the user config
  const widgetConfig = {
    theme: deepMerge(DEFAULT_CONFIG.theme, parsedUserConfig.theme || {}),
    context: parsedUserConfig.context,
    customContext: parsedUserConfig.customContext,
    contactEmail: parsedUserConfig.contactEmail || DEFAULT_CONFIG.contactEmail,
    behavior: deepMerge(
      DEFAULT_CONFIG.behavior,
      parsedUserConfig.behavior || {}
    ),
    features: deepMerge(
      DEFAULT_CONFIG.features,
      parsedUserConfig.features || {}
    ),
    ui: deepMerge(DEFAULT_CONFIG.ui, parsedUserConfig.ui || {}),
    customComponents: deepMerge(
      DEFAULT_CONFIG.customComponents,
      parsedUserConfig.customComponents || {}
    ),
    gtm: deepMerge(DEFAULT_CONFIG.gtm, parsedUserConfig.gtm || {}),
  };

  // Debug logs removed for production

  // Validate the widget config against the schema
  const parsedWidgetConfig = ConfigSchemas.Widget.parse(widgetConfig);

  // Convert function strings to actual functions for 'hidden' fields that have function defaultValue
  // Function type fields are already converted above (before validation)
  // This happens once at config load time, so runtime resolution is simpler
  if (parsedWidgetConfig.features?.chat?.fields) {
    parsedWidgetConfig.features.chat.fields =
      parsedWidgetConfig.features.chat.fields.map(field => {
        // Hidden type fields: defaultValue can be string or function
        // Convert if it's a function string
        if (field.type === 'hidden' && typeof field.defaultValue === 'string') {
          const converted = convertFunctionString(field.defaultValue);
          // Only convert if it's actually a function string, otherwise keep as string
          if (typeof converted === 'function') {
            return {
              ...field,
              defaultValue: converted as unknown as typeof field.defaultValue,
            };
          }
        }
        return field;
      });
  }

  // Add apiUrl and target field after validation (not part of Zod schema)
  const userConfigWithTarget = parsedUserConfig as Config.UserWidget;
  return {
    ...parsedWidgetConfig,
    apiUrl: process.env.WIDGET_API_URL || 'http://localhost:3001',
    ui: {
      ...parsedWidgetConfig.ui,
      target: userConfigWithTarget.ui?.target,
    },
  };
}

// Generate CSS custom properties from theme
export function generateThemeCSS(theme: Config.Theme): string {
  const parsedTheme = ConfigSchemas.Theme.parse(theme);

  return `
    --peptalk-color-primary: ${parsedTheme.colors.primary};
    --peptalk-color-secondary: ${parsedTheme.colors.secondary};
    --peptalk-color-neutrals-primary-900: ${parsedTheme.colors.neutrals.primary['900']};
    --peptalk-color-neutrals-primary-900-disabled: ${parsedTheme.colors.neutrals.primary['900-disabled']};
    --peptalk-color-neutrals-primary-850: ${parsedTheme.colors.neutrals.primary['850']};
    --peptalk-color-neutrals-primary-750: ${parsedTheme.colors.neutrals.primary['750']};
    --peptalk-color-neutrals-secondary-900: ${parsedTheme.colors.neutrals.secondary['900']};
    --peptalk-color-neutrals-secondary-750: ${parsedTheme.colors.neutrals.secondary['750']};
    
    --peptalk-color-surface: ${parsedTheme.colors.surface};
    
    --peptalk-color-text-primary: ${parsedTheme.colors.text.primary};
    --peptalk-color-text-secondary: ${parsedTheme.colors.text.secondary};
    --peptalk-color-text-tertiary: ${parsedTheme.colors.text.tertiary};
    --peptalk-color-text-disabled: ${parsedTheme.colors.text.disabled};
    --peptalk-color-text-inverse: ${parsedTheme.colors.text.inverse};
    
    --peptalk-color-border-primary: ${parsedTheme.colors.border.primary};
    --peptalk-color-border-secondary: ${parsedTheme.colors.border.secondary};
    --peptalk-color-border-focus: ${parsedTheme.colors.border.focus};
    --peptalk-color-border-hover: ${parsedTheme.colors.border.hover};

    --peptalk-color-success: ${parsedTheme.colors.status.success};
    --peptalk-color-warning: ${parsedTheme.colors.status.warning};
    --peptalk-color-error: ${parsedTheme.colors.status.error};
    --peptalk-color-info: ${parsedTheme.colors.status.info};

    --peptalk-color-user-bubble: ${parsedTheme.colors.chat.userBubble};
    --peptalk-color-agent-bubble: ${parsedTheme.colors.chat.agentBubble};
    --peptalk-color-system-bubble: ${parsedTheme.colors.chat.systemBubble};

    
    --peptalk-spacing-xs: ${parsedTheme.spacing.xs};
    --peptalk-spacing-sm: ${parsedTheme.spacing.sm};
    --peptalk-spacing-md: ${parsedTheme.spacing.md};
    --peptalk-spacing-lg: ${parsedTheme.spacing.lg};
    --peptalk-spacing-xl: ${parsedTheme.spacing.xl};
    --peptalk-spacing-xxl: ${parsedTheme.spacing.xxl};
    --peptalk-spacing-xxxl: ${parsedTheme.spacing.xxxl};
    
    --peptalk-font-family-primary: ${parsedTheme.typography.fontFamily.primary};
    --peptalk-font-family-secondary: ${parsedTheme.typography.fontFamily.secondary};
    --peptalk-font-family-mono: ${parsedTheme.typography.fontFamily.mono};
    
    --peptalk-font-size-xs: ${parsedTheme.typography.fontSize.xs};
        --peptalk-font-size-sm: ${parsedTheme.typography.fontSize.sm};
    --peptalk-font-size-base: ${parsedTheme.typography.fontSize.base};
    --peptalk-font-size-lg: ${parsedTheme.typography.fontSize.lg};
    --peptalk-font-size-xl: ${parsedTheme.typography.fontSize.xl};
    --peptalk-font-size-2xl: ${parsedTheme.typography.fontSize['2xl']};
    --peptalk-font-size-3xl: ${parsedTheme.typography.fontSize['3xl']};
    
    --peptalk-font-weight-light: ${parsedTheme.typography.fontWeight.light};
      --peptalk-font-weight-normal: ${parsedTheme.typography.fontWeight.normal};
    --peptalk-font-weight-medium: ${parsedTheme.typography.fontWeight.medium};
    --peptalk-font-weight-semibold: ${parsedTheme.typography.fontWeight.semibold};
    --peptalk-font-weight-bold: ${parsedTheme.typography.fontWeight.bold};
    
    --peptalk-line-height-tight: ${parsedTheme.typography.lineHeight.tight};
    --peptalk-line-height-normal: ${parsedTheme.typography.lineHeight.normal};
    --peptalk-line-height-relaxed: ${parsedTheme.typography.lineHeight.relaxed};
    
    --peptalk-border-radius-none: ${parsedTheme.borderRadius.none};
        --peptalk-border-radius-sm: ${parsedTheme.borderRadius.sm};
    --peptalk-border-radius-base: ${parsedTheme.borderRadius.base};
    --peptalk-border-radius-md: ${parsedTheme.borderRadius.md};
    --peptalk-border-radius-lg: ${parsedTheme.borderRadius.lg};
    --peptalk-border-radius-xl: ${parsedTheme.borderRadius.xl};
    --peptalk-border-radius-2xl: ${parsedTheme.borderRadius['2xl']};
    --peptalk-border-radius-max: ${parsedTheme.borderRadius.max};
    --peptalk-border-radius-full: ${parsedTheme.borderRadius.full};
    
    --peptalk-shadow-none: ${parsedTheme.shadows.none};
    --peptalk-shadow-sm: ${parsedTheme.shadows.sm};
    --peptalk-shadow-base: ${parsedTheme.shadows.base};
    --peptalk-shadow-md: ${parsedTheme.shadows.md};
    --peptalk-shadow-lg: ${parsedTheme.shadows.lg};
    --peptalk-shadow-xl: ${parsedTheme.shadows.xl};
    --peptalk-shadow-inner: ${parsedTheme.shadows.inner};
    
    --peptalk-transition-fast: ${parsedTheme.transitions.fast};
    --peptalk-transition-base: ${parsedTheme.transitions.base};
    --peptalk-transition-slow: ${parsedTheme.transitions.slow};
    --peptalk-transition-slower: ${parsedTheme.transitions.slower};
    
    --peptalk-z-index-base: ${parsedTheme.zIndex.base};
    --peptalk-z-index-elevated: ${parsedTheme.zIndex.elevated};
    --peptalk-z-index-sticky: ${parsedTheme.zIndex.sticky};
    --peptalk-z-index-overlay: ${parsedTheme.zIndex.overlay};
    --peptalk-z-index-modal: ${parsedTheme.zIndex.modal};
    --peptalk-z-index-popover: ${parsedTheme.zIndex.popover};
    --peptalk-z-index-tooltip: ${parsedTheme.zIndex.tooltip};
    --peptalk-z-index-toast: ${parsedTheme.zIndex.toast};
  `;
}
